import React, { useCallback, useContext, useMemo } from "react";
import clsx from "clsx";

import Text, { TextProps } from "../Text/Text";
import { LocaleContext, TextElement } from "../../../lib/util/LocaleUtils";
import * as strings from "../../../strings";
import * as DateUtils from "../../../lib/util/DateUtils";
import { ElementType } from "../../../lib/types/UtilTypes";

type BaseDurationProps<C extends ElementType = "span"> = TextProps<C> & {
	format?: string;
	className?: string;
	fallback?: TextElement | React.ReactNode;
	ago?: boolean;
};

type DurationPropsWithValue<C extends ElementType = "span"> =
	BaseDurationProps<C> & {
		value?: number | string | null;
	};

type DurationPropsWithSpan<C extends ElementType = "span"> =
	BaseDurationProps<C> & {
		from?: number;
		to?: number;
	};

export type DurationProps<C extends ElementType = "span"> =
	| DurationPropsWithSpan<C>
	| DurationPropsWithValue<C>;

function Duration<C extends ElementType = "span">(
	props: DurationProps<C>,
): JSX.Element {
	const { ago, value, from, to, format, className, fallback, ...otherProps } =
		props as DurationPropsWithValue<C> & DurationPropsWithSpan<C>;

	const { dateCode } = useContext(LocaleContext);

	const formattedDuration = useMemo(() => {
		const showAgo =
			Boolean(ago) &&
			(Boolean(value) || (from || Date.now()) < (to || Date.now()));

		return (
			DateUtils.formatDuration({
				value,
				from,
				to,
				ago: showAgo,
				format,
			}) ?? fallback
		);
		// eslint-disable-next-line
	}, [value, format, from, to, ago, dateCode, fallback]);

	const handleRenderText = useCallback(
		(text) => (!dateCode ? `${text} (localized)` : text),
		[dateCode],
	);

	return (
		<Text
			className={clsx("Duration", className)}
			{...otherProps}
			map={handleRenderText}
		>
			{formattedDuration ? formattedDuration : fallback || strings.MISC_NONE}
		</Text>
	);
}

export default React.memo(Duration);
